import { useEffect, useRef, useState } from 'react';
import { Linking } from 'react-native';
import ConsentModalContent from '@/components/SharedComponents/ConsentModalContent/ConsentModalContent';
import constants from '@/config/constants';
import { postConsents } from '@/api/services/consents';
import { startCheckConsent } from '@/utils/consents';
import { useDialog } from '@/hooks/useDialog';
import store, { useStoreState } from '@/stores/store';
import { logError } from '@/utils/logHandling';
import { IS_WEB, IS_ANDROID, IS_IOS } from '@/utils/platform';
import { DialogOptions } from '@/components/ui/shared/Dialog/types';

const appEnvironment = constants.appMode as 'therapist' | 'patient';
const storeName = IS_ANDROID ? 'Play Store' : IS_IOS ? 'App Store' : '';

const appLinks = {
  therapist: {
    ios: 'itms-apps://apps.apple.com/de/app/neolexon-aphasie/id1197504761',
    android: 'market://details?id=com.neolexon.therapeut',
  },
  patient: {
    ios: 'itms-apps://apps.apple.com/de/app/neolexon-aphasie/id1169666941',
    android: 'market://details?d=com.ionicframework.patientapp613489',
  },
};

const openStore = () => {
  let url = '';
  if (IS_ANDROID) {
    url = appLinks[appEnvironment].android;
  } else if (IS_IOS) {
    url = appLinks[appEnvironment].ios;
  }
  if (url) {
    Linking.openURL(url).catch((err) => {
      logError('Failed to open URL:', err);
    });
  }
};

const updateContent = IS_WEB
  ? 'Bitte aktualisieren Sie Ihre neolexon App'
  : `Bitte aktualisieren Sie Ihre neolexon App über den ${storeName}`;
const updateConfirmLabel = IS_WEB ? 'Aktualisieren' : `${storeName} öffnen`;
const onUpdatePress = IS_WEB ? () => window.location.reload() : openStore;

// Mounted inside DialogProvider: watches the app-shell store flags and pushes
// one shell dialog at a time (by priority) onto the shared stack. Renders null.
export function AppShellDialogs() {
  const {
    showDialog,
    removeDialog,
    removeAllDialogs,
    updateDialog,
    openDialogIds,
  } = useDialog();
  const {
    showConsentModal,
    showAppNeedToBeUpdatedModal,
    showCriticalAppUpdateModal,
    isTherapistDemo,
    isPatientDemo,
    isAuthenticated,
  } = useStoreState((state) => ({
    showConsentModal: state.showConsentModal,
    showAppNeedToBeUpdatedModal: state.showAppNeedToBeUpdatedModal,
    showCriticalAppUpdateModal: state.showCriticalAppUpdateModal,
    isTherapistDemo: state.isTherapistDemo,
    isPatientDemo: state.isPatientDemo,
    isAuthenticated: state.isAuthenticated,
  }));

  useEffect(() => {
    if (!isAuthenticated) {
      removeAllDialogs();
    }
  }, [isAuthenticated]);
  const [forceCloseConsent, setForceCloseConsent] = useState(false);
  const [showNoConnection, setShowNoConnection] = useState(false);
  const noConnectionTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // The shell dialog we own; `sig` gates in-place updates to real content changes.
  const shown = useRef<{ key: string; id: number; sig: string } | null>(null);

  // On unmount: clear the pending timer and remove our dialog so it isn't orphaned.
  useEffect(() => {
    return () => {
      if (noConnectionTimer.current) {
        clearTimeout(noConnectionTimer.current);
      }
      if (shown.current) {
        removeDialog(shown.current.id);
        shown.current = null;
      }
    };
  }, []);

  const consentConfirm = async () => {
    try {
      await postConsents(showConsentModal?.document);
      store.getActions().setShowConsentModal({
        document: '',
        buttonText: '',
        show: false,
      });
      startCheckConsent();
    } catch (error) {
      logError('Could not post consent', error);
      setForceCloseConsent(true);
      noConnectionTimer.current = setTimeout(() => {
        setShowNoConnection(true);
      }, 500);
      store.getActions().setShowConsentModal({
        document: '',
        buttonText: '',
        show: false,
      });
    }
  };

  const consentVisible =
    !isTherapistDemo &&
    !isPatientDemo &&
    !!showConsentModal?.show &&
    showConsentModal?.buttonText !== '' &&
    !forceCloseConsent;

  // Mutually exclusive by design; highest priority wins if several are set.
  let activeKey: string | null = null;
  let activeOptions: DialogOptions | null = null;
  if (showCriticalAppUpdateModal) {
    activeKey = 'critical';
    activeOptions = {
      title: 'App-Update erforderlich',
      content: updateContent,
      confirmAction: { label: updateConfirmLabel, onPress: onUpdatePress },
    };
  } else if (showAppNeedToBeUpdatedModal) {
    activeKey = 'update';
    activeOptions = {
      title: 'App-Update verfügbar',
      content: updateContent,
      confirmAction: { label: updateConfirmLabel, onPress: onUpdatePress },
      showCancelButton: true,
      cancelAction: {
        label: 'Abbrechen',
        onPress: () => store.getActions().setShowAppNeedToBeUpdatedModal(false),
      },
    };
  } else if (showNoConnection) {
    activeKey = 'noconn';
    activeOptions = {
      title: 'Verbindungsfehler',
      content:
        'Bitte überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
      confirmAction: {
        label: 'Schließen',
        onPress: () => {
          setShowNoConnection(false);
          setForceCloseConsent(false);
        },
      },
    };
  } else if (consentVisible) {
    activeKey = 'consent';
    activeOptions = {
      confirmAction: {
        label: showConsentModal?.buttonText ?? 'Ok',
        onPress: consentConfirm,
      },
      contentComponent: () => (
        <ConsentModalContent consentText={showConsentModal?.document?.uri} />
      ),
    };
  }

  // Dynamic content of the active dialog (only consent has any), so the effect
  // re-runs and refreshes when it changes.
  const signature =
    activeKey === 'consent'
      ? `consent|${showConsentModal?.document?.uri ?? ''}|${
          showConsentModal?.buttonText ?? ''
        }`
      : (activeKey ?? 'none');

  // False once someone else removed our dialog (removeAllDialogs, keyless pop)
  // → we forget it below and re-push while the flag is still active.
  const shownPresent =
    !!shown.current && openDialogIds.includes(shown.current.id);

  // Reconcile our one shell dialog by id, so we never touch a stacked feature dialog.
  useEffect(() => {
    if (shown.current && !shownPresent) {
      shown.current = null;
    }
    if (!activeKey || !activeOptions) {
      if (shown.current) {
        removeDialog(shown.current.id);
        shown.current = null;
      }
      return;
    }
    if (!shown.current) {
      shown.current = {
        key: activeKey,
        id: showDialog(activeOptions),
        sig: signature,
      };
    } else if (shown.current.key !== activeKey) {
      removeDialog(shown.current.id);
      shown.current = {
        key: activeKey,
        id: showDialog(activeOptions),
        sig: signature,
      };
    } else if (shown.current.sig !== signature) {
      // Same dialog, content changed → refresh in place.
      updateDialog(shown.current.id, activeOptions);
      shown.current.sig = signature;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, signature, shownPresent]);

  return null;
}
